/**
 * Navegação do portal público — cabeçalho e rodapé, declarados uma vez só.
 *
 * Existe como dado, e não como JSX espalhado pela casca, por dois motivos:
 *
 * 1. o cabeçalho do protótipo tem um nível de submenu ("Transparência" abre
 *    "Dados Públicos" e "Legislação"), e árvore desenhada à mão dentro do
 *    componente é onde os itens somem sem ninguém notar — foi o que aconteceu;
 * 2. assim a conformidade com o protótipo vira teste automático, como já é para
 *    as cinco áreas internas (`nucleo/navegacao/conformidadeComOPrototipo.spec.ts`).
 *
 * Decisão do PO em 2026-09-05: a árvore volta a ser a do protótipo, com o
 * dropdown de Transparência, os quatro itens que tinham sumido e o rodapé
 * institucional de nove links.
 *
 * Decisão do dono em 2026-09-30 (D1): a árvore passa a ter três entradas que
 * o protótipo não tem — "Resultados de sorteios" no submenu de Transparência e
 * no rodapé, e "Verificar documento" no rodapé. O protótipo não tem nenhuma
 * das duas telas, e as duas só se alcançavam por caminho indireto (a lista de
 * sorteios, por um cartão dentro de Dados Públicos; o verificador, por link
 * nenhum). Resultado de sorteio é ato que precisa de publicidade fácil, e o
 * verificador de documento é serviço ao cidadão. Fora essas três, a árvore
 * continua a do protótipo, e os acréscimos estão registrados, com o motivo, em
 * `ACRESCIMOS_AO_PROTOTIPO` (`conformidadeComOPrototipo.spec.ts`).
 */

import { ClipboardList, LogIn, PlayCircle, Search, type LucideIcon } from 'lucide-react';

import {
  CAMINHO_DO_ACOMPANHAMENTO,
  CAMINHO_DO_AUTOCADASTRO,
  CAMINHO_PARA_ENTRAR,
} from './credenciamento/AcompanhamentoContrato';

/** Coluna do rodapé em que o item aparece. Ver `SECOES_DO_RODAPE`. */
export type SecaoDoRodape = 'servicos' | 'institucional' | 'contato';

export type ItemPublico = {
  caminho: string;
  titulo: string;
  /** Casa a rota só quando idêntica — usado pela raiz, que prefixa tudo. */
  exato?: boolean;
  /**
   * Coluna do rodapé. É campo, e não array aninhado por coluna, de propósito:
   * `RODAPE_PUBLICO` continua sendo uma lista plana na ordem do protótipo, que é
   * o que `navegacaoPublica.spec.tsx` e `conformidadeComOPrototipo.spec.ts`
   * medem. Aninhar mudaria a forma que as duas conferências leem.
   */
  secao?: SecaoDoRodape;
  /** Mostrado em itens de submenu, abaixo do título (estilo do protótipo). */
  descricao?: string;
  /**
   * Submenu. O item pai NÃO é link — no protótipo (`PortalPublicoLayout.tsx`,
   * `fallbackNav`), o nó com `children` não tem `to` nenhum, só abre o menu.
   * `caminho` aqui serve só de identificador (chave de lista, verificação de
   * item ativo), nunca vira destino clicável quando há `filhos`.
   */
  filhos?: readonly ItemPublico[];
};

/**
 * Cabeçalho, na ordem do protótipo. "Entrar" não entra aqui: é o botão de ação
 * da direita, com tratamento próprio (some quando já existe sessão).
 *
 * "Resultados de sorteios" é o terceiro filho de Transparência, depois dos dois
 * do protótipo (D1, 2026-09-30): acréscimo vai ao fim do grupo, para não
 * deslocar a ordem que o protótipo dá aos itens que ele tem.
 */
export const CABECALHO_PUBLICO: readonly ItemPublico[] = [
  { caminho: '/', titulo: 'Início', exato: true },
  { caminho: '/programa', titulo: 'Sobre o TEG' },
  { caminho: '/credenciados', titulo: 'Para credenciados' },
  {
    caminho: '/transparencia',
    titulo: 'Transparência',
    filhos: [
      {
        caminho: '/dados-publicos',
        titulo: 'Dados Públicos',
        descricao: 'Indicadores, números e prestação de contas',
      },
      {
        caminho: '/legislacao',
        titulo: 'Legislação',
        descricao: 'Leis, decretos e portarias do programa',
      },
      {
        caminho: '/dados-publicos/sorteios',
        titulo: 'Resultados de sorteios',
        descricao: 'Contemplados, lista de espera e como conferir o resultado',
      },
    ],
  },
];

/**
 * As três colunas de links do rodapé, na ordem do protótipo.
 *
 * O protótipo agrupa os nove links em "Serviços", "Institucional" e
 * "Contato & LGPD" (`PortalPublicoLayout.tsx`, `DynamicFooter`). O agrupamento
 * é informação — diz ao cidadão se o link resolve um pedido dele, explica o
 * programa ou trata dos seus dados —, então vem junto com os links.
 */
export const SECOES_DO_RODAPE: readonly { chave: SecaoDoRodape; titulo: string }[] = [
  { chave: 'servicos', titulo: 'Serviços' },
  { chave: 'institucional', titulo: 'Institucional' },
  { chave: 'contato', titulo: 'Contato e LGPD' },
];

/**
 * Rodapé institucional: os nove links do protótipo, na ordem dele, e mais dois.
 *
 * Dois dos nove levam a telas que este repositório já tinha com endereço de
 * jornada — "Acompanhar inscrição" e "Consultar credenciados". Aqui elas ganham
 * o endereço público do protótipo, e o antigo continua respondendo.
 *
 * Os dois a mais vêm da decisão do dono de 2026-09-30 (D1): "Verificar
 * documento" fecha a coluna Serviços, depois de "Acompanhar inscrição", porque
 * é serviço que o cidadão usa com um documento na mão; "Resultados de sorteios"
 * entra em Institucional logo depois de "Consultar credenciados", a outra
 * consulta pública de Dados Públicos. Cada um vai ao fim da sequência do
 * protótipo na sua coluna, então a ordem relativa dos nove não muda.
 *
 * A ordem plana desta lista **já é** a ordem das três colunas lidas de cima a
 * baixo, da esquerda para a direita. Por isso `secao` não reordena nada: ela só
 * diz onde cada item cai na grade — e é por isso que os acréscimos entram na
 * posição da coluna deles, não no fim da lista.
 */
export const RODAPE_PUBLICO: readonly ItemPublico[] = [
  { caminho: '/credenciados', titulo: 'Para Credenciados', secao: 'servicos' },
  { caminho: '/credenciados/acompanhamento', titulo: 'Acompanhar inscrição', secao: 'servicos' },
  { caminho: '/verificar-documento', titulo: 'Verificar documento', secao: 'servicos' },
  { caminho: '/programa', titulo: 'Sobre o TEG', secao: 'institucional' },
  { caminho: '/legislacao', titulo: 'Legislação', secao: 'institucional' },
  { caminho: '/dados-publicos', titulo: 'Dados Públicos', secao: 'institucional' },
  {
    caminho: '/dados-publicos/credenciados',
    titulo: 'Consultar credenciados',
    secao: 'institucional',
  },
  {
    caminho: '/dados-publicos/sorteios',
    titulo: 'Resultados de sorteios',
    secao: 'institucional',
  },
  { caminho: '/privacidade', titulo: 'Política de Privacidade', secao: 'contato' },
  { caminho: '/termos-de-uso', titulo: 'Termos de Uso', secao: 'contato' },
  { caminho: '/acessibilidade', titulo: 'Acessibilidade', secao: 'contato' },
];

/**
 * Consultas públicas do sistema, fixas numa página institucional, por slug.
 *
 * O corpo das páginas institucionais é conteúdo editorial, que a Secretaria
 * escreve e reescreve pela Administração; as consultas não são texto, são
 * telas do sistema com endereço próprio. Por isso ficam aqui, e não no
 * conteúdo: o editor não as apaga por engano, e nenhum link aponta para uma
 * tela que não existe.
 *
 * Dados Públicos (D-32): a consulta de credenciados e, ao lado dela, os
 * resultados dos sorteios do credenciamento. As descrições resumem o que a
 * própria tela de destino diz no cabeçalho.
 *
 * Legislação (D2, dono em 2026-09-30): as publicações vigentes — legislação,
 * chamamentos e avisos que a Administração publica com vigência. A tela
 * `/publicacoes` saiu do cabeçalho em 2026-09-06, pela fidelidade ao
 * protótipo, e desde então não tinha link nenhum; aqui ela ganha entrada sem
 * mexer na árvore do protótipo. Por ela se chega também à consulta pelo código
 * público (`/jornadas/j02/s03-consultar-publico`), que só é linkada de lá.
 */
export const CONSULTAS_POR_PAGINA: Readonly<Record<string, readonly ItemPublico[]>> = {
  'dados-publicos': [
    {
      caminho: '/dados-publicos/credenciados',
      titulo: 'Consultar credenciados',
      descricao:
        'Pesquise credenciados no programa TEG. São exibidos apenas dados públicos, conforme a LGPD.',
    },
    {
      caminho: '/dados-publicos/sorteios',
      titulo: 'Resultados de sorteios',
      descricao:
        'O sorteio do credenciamento, ciclo a ciclo: quem foi contemplado, a lista de espera e como conferir o resultado.',
    },
  ],
  legislacao: [
    {
      caminho: '/publicacoes',
      titulo: 'Publicações vigentes',
      descricao:
        'Legislação, chamamentos e avisos em vigor, cada um com a versão, a vigência e o código público para consulta.',
    },
  ],
};

/**
 * O cartão "Próximo passo" da página Para credenciados: as portas de entrada do
 * transportador no credenciamento, iguais às do protótipo
 * (`CredenciadosCtaPanel.tsx`).
 *
 * Mora aqui, ao lado de `CONSULTAS_POR_PAGINA`, pelo mesmo motivo: são telas do
 * sistema com endereço próprio, não texto editorial. Se viessem das seções da
 * página, o editor da Administração poderia apagá-las por engano. Declarado
 * como dado, o teste confere que cada destino tem rota escrita no `App.tsx`.
 *
 * Aqui ficam as portas de quem ainda não entrou: a ação principal e o menu
 * "Ver outras opções". As outras versões do cartão (credenciado, pré-credenciado,
 * inscrito) dependem de sessão e são montadas em `ProximoPassoCredenciamento.tsx`.
 *
 * Uma troca em relação ao protótipo: "Retomar rascunho salvo" levava a
 * `/credenciamento/retomar`, que saiu (CLAUDE.md, exceção do portal público
 * encerrada em 2026-09-28): o rascunho mora no servidor e retomar é entrar de
 * novo. O item continua, com o texto do protótipo, e leva à entrada.
 */
export type OpcaoDoProximoPasso = ItemPublico & { icone: LucideIcon };

export const PROXIMO_PASSO_CREDENCIAMENTO: {
  slug: string;
  inicio: OpcaoDoProximoPasso;
  outrasOpcoes: readonly OpcaoDoProximoPasso[];
} = {
  slug: 'credenciados',
  inicio: { caminho: CAMINHO_DO_AUTOCADASTRO, titulo: 'Iniciar nova inscrição', icone: PlayCircle },
  outrasOpcoes: [
    {
      caminho: CAMINHO_DO_ACOMPANHAMENTO,
      titulo: 'Consultar por protocolo',
      descricao: 'Acompanhar uma inscrição já enviada',
      icone: Search,
    },
    {
      caminho: CAMINHO_PARA_ENTRAR,
      titulo: 'Retomar rascunho salvo',
      descricao: 'Continuar uma inscrição ainda não enviada',
      icone: ClipboardList,
    },
    {
      caminho: CAMINHO_PARA_ENTRAR,
      titulo: 'Acessar minha conta',
      descricao: 'Entrar com CPF e senha',
      icone: LogIn,
    },
  ],
};

/**
 * Páginas institucionais servidas pelo conteúdo editorial, por endereço.
 *
 * O editor da Administração já grava página e seções; faltava a rota pública
 * que as mostra. O endereço canônico de qualquer página criada lá é
 * `/portal/:slug` — estes são os apelidos fixos que o protótipo usa no menu.
 */
export const PAGINAS_INSTITUCIONAIS: readonly { caminho: string; slug: string }[] = [
  { caminho: '/programa', slug: 'programa' },
  { caminho: '/credenciados', slug: 'credenciados' },
  { caminho: '/dados-publicos', slug: 'dados-publicos' },
  { caminho: '/legislacao', slug: 'legislacao' },
  { caminho: '/termos-de-uso', slug: 'termos-de-uso' },
];
